import BaseApiClient from '@api-client/BaseApiClient';

export type TextContent = {
  type: 'text';
  text: string;
};

export type TableContent = {
  type: 'table';
  table: {
    title?: string;
    query_id?: string;
    tool_use_id?: string;
    result_set: {
      data: Array<Array<any>>;
      resultSetMetaData: {
        format: string;
        numRows: number;
        partition: number;
        rowType: Array<{
          name: string;
          type: string;
          nullable?: boolean;
          length?: number;
          precision?: number;
          scale?: number;
        }>;
      };
    };
  };
};

export type ChartContent = {
  type: 'chart';
  chart: {
    title?: string;
    mark: string;
    encoding?: Record<string, any>;
    data: {
      values: Array<Record<string, any>>;
    };
    config?: Record<string, any>;
  };
};

export type ThinkingContent = {
  type: 'thinking';
  thinking: {
    text: string;
  };
};

export type ToolUseContent = {
  type: 'tool_use';
  tool_use: Record<string, any>;
};

export type ToolResultContent = {
  type: 'tool_result';
  tool_result: Record<string, any>;
};

export type ResponseContent =
  | TextContent
  | TableContent
  | ChartContent
  | ThinkingContent
  | ToolUseContent
  | ToolResultContent;

export type BackendResponse = {
  content: ResponseContent[];
  role: string;
  schema_version: string;
};

export type ChatLine = {
  id: number;
  chat_id?: number; // Optional since ChatLineListResource doesn't include it
  query: string;
  response: string | ResponseContent[] | BackendResponse;
};

export type Chat = {
  id: number;
  name?: string;
  created_at?: string;
  updated_at?: string;
  chat_lines?: ChatLine[];
};

export class ApiChat extends BaseApiClient {
  /**
   * Get all chats
   */
  async list(): Promise<Chat[]> {
    return new Promise((resolve, reject) => {
      this.client
        .get('/chats', {
          headers: {
            Version: '2025-11-14'
          }
        })
        .then((res) => {
          if (res.data) {
            // Backend returns paginated response: { data: [...], meta: {...} }
            resolve(res.data.data || []);
          } else {
            reject('Invalid response');
          }
        })
        .catch((err) => reject(err));
    });
  }

  /**
   * Get a single chat by ID
   */
  async get(chatId: number): Promise<Chat> {
    return new Promise((resolve, reject) => {
      this.client
        .get(`/chats/${chatId}`, {
          params: {
            include: 'chatLines'
          },
          headers: {
            Version: '2025-11-14'
          }
        })
        .then((res) => {
          if (res.data) {
            resolve(res.data.data || res.data);
          } else {
            reject('Invalid response');
          }
        })
        .catch((err) => reject(err));
    });
  }

  /**
   * Create a new chat
   */
  async create(dto?: { name?: string }): Promise<Chat> {
    return new Promise((resolve, reject) => {
      this.client
        .post('/chats', dto || {}, {
          headers: {
            Version: '2025-11-14'
          }
        })
        .then((res) => {
          if (res.data) {
            resolve(res.data.data || res.data);
          } else {
            reject('Invalid response');
          }
        })
        .catch((err) => reject(err));
    });
  }

  /**
   * Update a chat
   */
  async update(chatId: number, dto: { name?: string }): Promise<Chat> {
    return new Promise((resolve, reject) => {
      this.client
        .put(`/chats/${chatId}`, dto, {
          headers: {
            Version: '2025-11-14'
          }
        })
        .then((res) => {
          if (res.data) {
            resolve(res.data.data || res.data);
          } else {
            reject('Invalid response');
          }
        })
        .catch((err) => reject(err));
    });
  }

  /**
   * Add a chat line to a chat
   */
  async addChatLine(chatId: number, dto: { query: string }): Promise<ChatLine> {
    return new Promise((resolve, reject) => {
      this.client
        .post(`/chats/${chatId}/chat-lines`, dto, {
          headers: {
            Version: '2025-11-14'
          }
        })
        .then((res) => {
          if (res.data) {
            resolve(res.data.data || res.data);
          } else {
            reject('Invalid response');
          }
        })
        .catch((err) => reject(err));
    });
  }
}

export { chatQueryKeys } from './query-keys';
export { useChatListQuery, useChatQuery } from './use-chat-queries';
export {
  useChatAddLineMutation,
  useChatCreateMutation,
  useChatUpdateMutation
} from './use-chat-mutations';

